import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
// @ts-ignore
import bpmnlintConfig from './bundled-config.js';

export interface BpmnLintIssue {
  id: string;
  message: string;
  severity: 'error' | 'warn' | 'info';
  elementId?: string;
  elementType?: string;
  rule?: string;
  path?: string[];
  meta?: {
    documentation?: {
      url?: string;
    };
  };
}

export interface BpmnLintResult {
  issues: BpmnLintIssue[];
  hasErrors: boolean;
  hasWarnings: boolean;
  hasInfo: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BpmnLintService {
  private readonly _lintResults = new BehaviorSubject<BpmnLintResult>({
    issues: [],
    hasErrors: false,
    hasWarnings: false,
    hasInfo: false,
  });

  readonly lintResults$ = this._lintResults.asObservable();

  /**
   * Get the BPMN lint configuration bundle
   */
  getBpmnlintBundle() {
    return bpmnlintConfig;
  }

  /**
   * Update lint results
   */
  updateLintResults(results: BpmnLintResult): void {
    this._lintResults.next(results);
  }

  /**
   * Clear lint results
   */
  clearLintResults(): void {
    this._lintResults.next({
      issues: [],
      hasErrors: false,
      hasWarnings: false,
      hasInfo: false,
    });
  }

  /**
   * Get current lint results
   */
  getCurrentLintResults(): BpmnLintResult {
    return this._lintResults.value;
  }

  /**
   * Check if there are any linting issues
   */
  hasLintIssues(): boolean {
    const results = this._lintResults.value;
    return results.hasErrors || results.hasWarnings || results.hasInfo;
  }

  /**
   * Get lint issues by severity
   */
  getIssuesBySeverity(severity: 'error' | 'warn' | 'info'): BpmnLintIssue[] {
    return this._lintResults.value.issues.filter(
      (issue) => issue.severity === severity
    );
  }

  /**
   * Get error count
   */
  getErrorCount(): number {
    return this.getIssuesBySeverity('error').length;
  }

  /**
   * Get warning count
   */
  getWarningCount(): number {
    return this.getIssuesBySeverity('warn').length;
  }

  /**
   * Get info count
   */
  getInfoCount(): number {
    return this.getIssuesBySeverity('info').length;
  }
}
